import { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  LayoutGrid,
  Wrench,
  XCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { client } from "@/lib/api-base";
import {
  SUITE_BRAND,
  SUITE_MODULES,
  CURRENT_MODULE_KEY,
  CURRENT_MODULE,
  ACTIVE_MODULES,
  IN_DEV_MODULES,
} from "@/config/suite";

/**
 * SuiteLauncher — v1.60.6 · Portale d'ingresso della Gruppo Impresa Suite.
 *
 * Non è più un elenco compatto di link ma un vero "hub applicazioni":
 * ogni modulo è una card con icona ufficiale, nome, descrizione, badge
 * di stato e pulsante di apertura esplicito. L'applicazione corrente
 * è visivamente distinta (bordo colorato + sfondo leggero + pulsante
 * disabilitato "Sei già qui").
 *
 * Stati modulo (fonte di verità: `/app/frontend/src/config/suite.js`):
 *   • active           → 🟢 Disponibile (pulsante "Apri")
 *   • active + current → 🟢 Applicazione corrente (pulsante "Sei già qui")
 *   • in-development   → 🟡 In sviluppo (pulsante disabilitato)
 *   • planned          → 🔴 Non disponibile (pulsante disabilitato)
 *
 * Scalabilità: aggiungere un nuovo modulo (CRM, Paghe, Documentale…)
 * richiede SOLO una entry in `SUITE_MODULES`. Nessuna modifica UI.
 */
export function SuiteLauncher({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [suiteMeta, setSuiteMeta] = useState(null);

  // Carica versione + ultimo aggiornamento dal backend quando il pannello
  // si apre (best-effort, silenzioso su errore).
  useEffect(() => {
    if (!open || suiteMeta) return;
    let alive = true;
    (async () => {
      try {
        const res = await client.get("/version");
        if (alive) setSuiteMeta(res.data);
      } catch {
        /* fallback: mostra solo la versione dal config */
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, suiteMeta]);

  const installedCount = ACTIVE_MODULES.length + IN_DEV_MODULES.length;
  const availableCount = ACTIVE_MODULES.length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="suite-launcher-trigger"
          title="Apri l'hub applicazioni della Suite"
          className={
            compact
              ? "flex w-full items-center gap-2 rounded-md border border-stone-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-stone-600 shadow-sm transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
              : "inline-flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 shadow-sm transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
          }
        >
          <LayoutGrid size={compact ? 12 : 14} strokeWidth={2} />
          <span className="flex-1 text-left">Applicazioni</span>
          <span className="text-[9px] font-semibold uppercase tracking-wider text-stone-400">
            Suite
          </span>
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="suite-launcher-panel"
        className="max-h-[90vh] max-w-2xl gap-0 overflow-hidden p-0"
      >
        {/* Header brand */}
        <DialogHeader className="border-b border-stone-100 bg-gradient-to-br from-primary/5 to-white px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
            >
              <LayoutGrid size={18} strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-[15px] font-semibold text-stone-900">
                Hub Applicazioni
              </DialogTitle>
              <div className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.15em] text-primary">
                {SUITE_BRAND.productName}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Griglia moduli */}
        <div className="max-h-[60vh] overflow-y-auto px-4 py-4">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {SUITE_MODULES.map((m) => (
              <ModuleCard
                key={m.key}
                module={m}
                current={m.key === CURRENT_MODULE_KEY}
                onOpen={() => setOpen(false)}
              />
            ))}
          </div>
        </div>

        {/* Footer riepilogo ecosistema.
            v1.60.6-final: rimosso "Suite v{n}" (era un placeholder senza
            versionamento ufficiale). La versione mostrata è ora quella
            REALE del modulo corrente, letta da GET /api/version — coerente
            con il footer sidebar. Nessuna confusione fra "versione Suite"
            e "versione del gestionale". */}
        <div className="border-t border-stone-100 bg-stone-50/60 px-6 py-3">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-600">
            Ecosistema {SUITE_BRAND.productName}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone-500">
            <span data-testid="suite-installed-count" className="tabular-nums">
              <strong className="font-semibold text-stone-700">
                {installedCount}
              </strong>{" "}
              moduli installati
            </span>
            <span className="text-stone-300" aria-hidden="true">
              ·
            </span>
            <span data-testid="suite-available-count" className="tabular-nums">
              <strong className="font-semibold text-emerald-700">
                {availableCount}
              </strong>{" "}
              disponibili
            </span>
            {suiteMeta?.version && (
              <>
                <span className="text-stone-300" aria-hidden="true">
                  ·
                </span>
                <span data-testid="current-module-version" className="tabular-nums">
                  {CURRENT_MODULE?.slug || "Modulo corrente"}{" "}
                  <strong className="font-semibold text-stone-700">
                    v{suiteMeta.version}
                  </strong>
                </span>
              </>
            )}
            {suiteMeta?.updated_at && (
              <>
                <span className="text-stone-300" aria-hidden="true">
                  ·
                </span>
                <span data-testid="suite-updated-at">
                  ultimo aggiornamento{" "}
                  {new Date(
                    suiteMeta.updated_at + "T00:00:00",
                  ).toLocaleDateString("it-IT", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Configurazione visiva unificata per i 4 stati card.
 * Convenzione icone/colori (⚠️ NON usare emoji nel DOM: `lucide-react`
 * per l'iconografia, colori Tailwind semantici per il badge).
 */
function statusVisual(status, isCurrent) {
  if (isCurrent) {
    return {
      key: "current",
      label: "Applicazione corrente",
      icon: CheckCircle2,
      badgeCls: "bg-emerald-100 text-emerald-800 border-emerald-200",
      dotCls: "bg-emerald-500",
    };
  }
  if (status === "active") {
    return {
      key: "available",
      label: "Disponibile",
      icon: CheckCircle2,
      badgeCls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dotCls: "bg-emerald-500",
    };
  }
  if (status === "in-development") {
    return {
      key: "in-development",
      label: "In sviluppo",
      icon: Wrench,
      badgeCls: "bg-amber-50 text-amber-800 border-amber-200",
      dotCls: "bg-amber-500",
    };
  }
  // planned → "Non disponibile"
  return {
    key: "unavailable",
    label: "Non disponibile",
    icon: XCircle,
    badgeCls: "bg-rose-50 text-rose-700 border-rose-200",
    dotCls: "bg-rose-500",
  };
}

function ModuleCard({ module, current, onOpen }) {
  const Icon = module.icon;
  const vis = statusVisual(module.status, current);
  const StatusIcon = vis.icon;
  const isActive = module.status === "active";
  const canOpen = isActive && !current;

  const handleOpen = () => {
    if (!canOpen) return;
    // Chiude il modal poi apre il modulo in una nuova tab (comportamento
    // atteso su launcher inter-app: non ruba il tab corrente).
    window.open(module.url, "_blank", "noopener,noreferrer");
    onOpen?.();
  };

  return (
    <div
      data-testid={`suite-card-${module.key}`}
      data-status={vis.key}
      className={
        "group flex flex-col rounded-lg border p-3 transition " +
        (current
          ? "border-primary/50 bg-primary/[0.04] shadow-sm ring-1 ring-primary/20"
          : "border-stone-200 bg-white hover:border-stone-300 hover:shadow-sm")
      }
      style={
        current ? { borderColor: module.color + "80" } : undefined
      }
    >
      {/* Riga superiore: icona + nome + badge */}
      <div className="mb-2 flex items-start gap-2.5">
        <div
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white shadow-sm"
          style={{ backgroundColor: module.color }}
        >
          <Icon size={18} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-stone-900">
            {module.name}
          </div>
          <span
            data-testid={`suite-status-${module.key}`}
            className={
              "mt-1 inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wider " +
              vis.badgeCls
            }
          >
            <span
              aria-hidden="true"
              className={"h-1.5 w-1.5 rounded-full " + vis.dotCls}
            />
            {vis.label}
          </span>
        </div>
      </div>

      {/* Tagline */}
      <p className="mb-3 line-clamp-2 min-h-[2.4em] text-[11px] leading-snug text-stone-500">
        {module.tagline}
      </p>

      {/* Riga inferiore: azione */}
      {current ? (
        <Button
          type="button"
          disabled
          data-testid={`suite-btn-${module.key}`}
          className="h-8 w-full cursor-default gap-1.5 bg-emerald-50 text-[11px] font-semibold text-emerald-800 shadow-none hover:bg-emerald-50"
          title="Stai già lavorando in questo modulo"
        >
          <CheckCircle2 size={12} strokeWidth={2.4} />
          Sei già qui
        </Button>
      ) : canOpen ? (
        <Button
          type="button"
          onClick={handleOpen}
          data-testid={`suite-btn-${module.key}`}
          className="h-8 w-full gap-1.5 text-[11px] font-semibold"
          style={{ backgroundColor: module.color }}
          title={`Apri ${module.name} in una nuova finestra`}
        >
          Apri <ArrowRight size={12} strokeWidth={2.4} />
          <ExternalLink size={10} strokeWidth={2.4} className="opacity-80" />
        </Button>
      ) : (
        <Button
          type="button"
          disabled
          data-testid={`suite-btn-${module.key}`}
          variant="outline"
          className="h-8 w-full cursor-not-allowed gap-1.5 text-[11px] font-semibold text-stone-400"
          title={
            module.status === "in-development"
              ? `${module.name} è in sviluppo`
              : `${module.name} non è ancora disponibile`
          }
        >
          <StatusIcon size={12} strokeWidth={2.4} />
          {module.status === "in-development" ? "In sviluppo" : "Non disponibile"}
        </Button>
      )}
    </div>
  );
}
